/** @module How the recognition decides: mode, thresholds and whether corrections feed back. */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { count } from "@/lib/format";
import { useRecognition, useUpdateRecognition } from "@/features/admin/use-admin";

export function RecognitionPanel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const recognition = useRecognition();
  const update = useUpdateRecognition();
  // Null means "show what the server says"; the first keystroke takes over from it.
  const [edited, setEdited] = useState<Record<string, string> | null>(null);

  if (recognition.isPending) return <p className="t-muted">{t("common.loading")}</p>;
  if (recognition.isError)
    return (
      <p role="alert" className="loss">
        {t("common.loadError")}
      </p>
    );

  const data = recognition.data;
  const draft =
    edited ??
    Object.fromEntries(Object.entries(data.thresholds).map(([key, value]) => [key, String(value)]));
  const numbers = Object.fromEntries(
    Object.entries(draft)
      .map(([key, value]) => [key, Number(value)] as const)
      .filter(([, value]) => Number.isFinite(value)),
  );
  const complete = Object.keys(numbers).length === Object.keys(draft).length;

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3 panel panel-lg">
        <h3 className="t-h3">{t("admin.recognition.mode")}</h3>
        <p className="t-sm t-muted">{t("admin.recognition.modeHint")}</p>
        <div className="flex flex-wrap gap-2">
          {(["calibration", "active"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={data.mode === mode}
              disabled={update.isPending}
              onClick={() => {
                update.mutate({ mode });
              }}
              className="min-h-(--touch) rounded-[var(--radius-pill)] border border-(--border) px-4 aria-pressed:border-(--accent) aria-pressed:bg-(--accent-soft) aria-pressed:text-(--accent-soft-ink)"
            >
              {t(`admin.recognition.modes.${mode}`)}
            </button>
          ))}
        </div>

        <label className="mt-2 flex items-center gap-3 text-[length:var(--text-sm)]">
          <input
            type="checkbox"
            checked={data.feedback_enabled}
            disabled={update.isPending}
            onChange={(event) => {
              update.mutate({ feedback_enabled: event.target.checked });
            }}
            className="size-5"
          />
          <span>{t("admin.recognition.feedback")}</span>
        </label>

        <label className="flex items-start gap-3 text-[length:var(--text-sm)]">
          <input
            type="checkbox"
            aria-label={t("admin.recognition.autoForUsers")}
            checked={data.auto_for_users}
            disabled={update.isPending}
            onChange={(event) => {
              update.mutate({ auto_for_users: event.target.checked });
            }}
            className="mt-0.5 size-5"
          />
          <span className="flex flex-col">
            <span>{t("admin.recognition.autoForUsers")}</span>
            <span className="t-xs t-muted">{t("admin.recognition.autoForUsersHint")}</span>
          </span>
        </label>
      </section>

      <section className="flex flex-col gap-3 panel panel-lg">
        <h3 className="t-h3">{t("admin.recognition.thresholds")}</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.keys(draft)
            .sort()
            .map((key) => (
              <label key={key} className="flex flex-col gap-1 text-[length:var(--text-sm)]">
                <span className="t-muted">{t(`admin.recognition.keys.${key}`, key)}</span>
                <input
                  type="number"
                  step="0.01"
                  value={draft[key]}
                  onChange={(event) => {
                    setEdited({ ...draft, [key]: event.target.value });
                  }}
                  className="num input"
                />
              </label>
            ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={update.isPending || !complete}
            onClick={() => {
              update.mutate({ thresholds: numbers });
            }}
            className="btn btn-primary"
          >
            {t("common.save")}
          </button>
          <button
            type="button"
            onClick={() => {
              setEdited(null);
            }}
            className="btn btn-secondary"
          >
            {t("admin.recognition.reset")}
          </button>
          {update.isError ? (
            <span role="alert" className="t-sm loss">
              {update.error instanceof Error ? update.error.message : t("common.saveError")}
            </span>
          ) : null}
        </div>
      </section>

      <section className="flex flex-col gap-2 panel panel-lg">
        <h3 className="t-h3">{t("admin.recognition.index")}</h3>
        <dl className="grid gap-x-6 gap-y-1 text-[length:var(--text-sm)] sm:grid-cols-2">
          <div className="flex justify-between gap-3">
            <dt className="t-muted">{t("admin.recognition.version")}</dt>
            <dd className="num">{data.active_index_version ?? "–"}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="t-muted">{t("admin.recognition.prints")}</dt>
            <dd className="num">{count(data.indexed_prints, locale)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="t-muted">{t("admin.recognition.vectors")}</dt>
            <dd className="num">{count(data.vectors, locale)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="t-muted">{t("admin.recognition.model")}</dt>
            <dd>
              {data.model_variant}
              {data.model_ready ? "" : ` · ${t("admin.recognition.modelMissing")}`}
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}
